import React, { useEffect, useState, useRef, useMemo } from "react";
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import DownloadablePdfSection from "./DownloadablePdfSection";

const TravelNarrativeRouteTablesPanel = ({
  podcastId,
  nodeId,
  selectedLanguage,

  canGenerate,
  isGenerating = false,
  onGenerate,
  onRegenerateRouteTables,

  travelExpNarrativeStream,
  setTravelExpNarrativeStream,

  routeTablesDraft,
  setRouteTablesDraft,

  autoChecklistItems,
  setAutoChecklistItems,

  promptAdditionCsv,
  setPromptAdditionCsv,
}) => {
  const [travelNarrativeDraft, setTravelNarrativeDraft] = useState("");
  const [isSavingNarrative, setIsSavingNarrative] = useState(false);
  const [narrativeSaveMsg, setNarrativeSaveMsg] = useState("");

  const [isSavingRouteTables, setIsSavingRouteTables] = useState(false);
  const [routeTablesSaveMsg, setRouteTablesSaveMsg] = useState("");

  const [isLoadingSaved, setIsLoadingSaved] = useState(false);

  const [isSavingPromptCsv, setIsSavingPromptCsv] = useState(false);
  const [promptCsvSaveMsg, setPromptCsvSaveMsg] = useState("");

  const narrativeRef = useRef(null);
  const routeTablesRef = useRef(null);


  const autoGrow = (el, maxPx = 260) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, maxPx) + "px";
  };

  useEffect(() => {
    autoGrow(narrativeRef.current, 300)
  }, [travelNarrativeDraft]);

  useEffect(() => {
    autoGrow(routeTablesRef.current, 220);
  }, [routeTablesDraft]);

  // Keep editor draft in sync when we load/regen a new narrative
  useEffect(() => {
    setTravelNarrativeDraft(String(travelExpNarrativeStream || ""));
  }, [nodeId, travelExpNarrativeStream]);

  // Load saved narrative + route tables whenever node changes
  useEffect(() => {
    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();
    if (!pid || !nid) return;

    const db = getFirestore();
    const narrativeRef = doc(db, "podcasts", pid, "travel_narrative", nid);
    const detailsRef = doc(db, "podcasts", pid, "podcast_details", nid);

    let cancelled = false;

    (async () => {
      setIsLoadingSaved(true);
      try {
        const [nSnap, dSnap] = await Promise.all([getDoc(narrativeRef), getDoc(detailsRef)]);
        if (cancelled) return;

        if (nSnap.exists()) {
          const narrative = String(nSnap.data()?.narrative || "");
          setTravelExpNarrativeStream(narrative);
          setTravelNarrativeDraft(narrative);
        } else {
          setTravelExpNarrativeStream("");
          setTravelNarrativeDraft("");
        }

        if (dSnap.exists()) {
          const d = dSnap.data() || {};

          if (d.routeTables != null) {
            try {
              setRouteTablesDraft(JSON.stringify(d.routeTables, null, 2));
            } catch {
              setRouteTablesDraft("");
            }
          } else {
            setRouteTablesDraft("");
          }

          if (Array.isArray(d.routeChecklistItems)) setAutoChecklistItems(d.routeChecklistItems);
          if (typeof d.promptAdditionCsv === "string") setPromptAdditionCsv(d.promptAdditionCsv);
        } else {
          setRouteTablesDraft("");
        }
      } catch (e) {
        console.error("Failed loading narrative/details:", e);
      } finally {
        if (!cancelled) setIsLoadingSaved(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    podcastId,
    nodeId,
    setTravelExpNarrativeStream,
    setRouteTablesDraft,
    setAutoChecklistItems,
    setPromptAdditionCsv,
  ]);

  const handleSavePromptCsvOnly = async () => {
    setPromptCsvSaveMsg("");
  
    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();
  
    if (!pid) return setPromptCsvSaveMsg("Missing podcastId.");
    if (!nid) return setPromptCsvSaveMsg("Pick a location first so we know the nearest node.");
  
    const csv = String(promptAdditionCsv || "").trim();
    if (!csv) return setPromptCsvSaveMsg("promptCsv is empty.");
  
    setIsSavingPromptCsv(true);
    try {
      const db = getFirestore();
      const detailsRef = doc(db, "podcasts", pid, "podcast_details", nid);
  
      await setDoc(
        detailsRef,
        {
          promptAdditionCsv: csv,
          promptAdditionCsvUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );
  
      setPromptCsvSaveMsg("Saved promptCsv ✅");
    } catch (e) {
      console.error(e);
      setPromptCsvSaveMsg("Failed to save promptCsv.");
    } finally {
      setIsSavingPromptCsv(false);
    }
  };

  const handleSaveNarrative = async () => {
    setNarrativeSaveMsg("");

    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();

    if (!pid) return setNarrativeSaveMsg("Missing podcastId.");
    if (!nid) return setNarrativeSaveMsg("Pick a location first so we know the nearest node.");

    setIsSavingNarrative(true);
    try {
      const db = getFirestore();
      const narrativeRef = doc(db, "podcasts", pid, "travel_narrative", nid);

      await setDoc(
        narrativeRef,
        {
          narrative: String(travelNarrativeDraft || ""),
          updatedAt: serverTimestamp(),
          nodeId: nid,
        },
        { merge: true }
      );

      setTravelExpNarrativeStream(String(travelNarrativeDraft || ""));
      setNarrativeSaveMsg("Saved ✅");
    } catch (e) {
      console.error(e);
      setNarrativeSaveMsg("Failed to save.");
    } finally {
      setIsSavingNarrative(false);
    }
  };

  const handleSaveRouteTables = async () => {
    setRouteTablesSaveMsg("");

    const pid = String(podcastId || "").trim();
    const nid = String(nodeId || "").trim();

    if (!pid) return setRouteTablesSaveMsg("Missing podcastId.");
    if (!nid) return setRouteTablesSaveMsg("Pick a location first so we know the nearest node.");

    let parsed = null;
    try {
      parsed = routeTablesDraft && routeTablesDraft.trim() ? JSON.parse(routeTablesDraft) : null;
    } catch (e) {
      return setRouteTablesSaveMsg("Route tables must be valid JSON.");
    }

    setIsSavingRouteTables(true);
    try {
      const db = getFirestore();
      const detailsRef = doc(db, "podcasts", pid, "podcast_details", nid);

      await setDoc(
        detailsRef,
        {
          routeTables: parsed,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setRouteTablesSaveMsg("Saved ✅");
    } catch (e) {
      console.error(e);
      setRouteTablesSaveMsg("Failed to save.");
    } finally {
      setIsSavingRouteTables(false);
    }
  };

  const { previewRows, routeTablesPreviewError } = useMemo(() => {
    const draft = String(routeTablesDraft || "").trim();
    if (!draft) return { previewRows: [], routeTablesPreviewError: "" };

    try {
      const parsed = JSON.parse(draft);
      const rows = Array.isArray(parsed) ? parsed : [];
      return { previewRows: rows, routeTablesPreviewError: "" };
    } catch (err) {
      return {
        previewRows: [],
        routeTablesPreviewError: "Route tables JSON is invalid, so a preview is unavailable."
      };
    }
  }, [routeTablesDraft]);

  const hasRouteTablesDraft = Boolean(String(routeTablesDraft || "").trim());
  const canRegenerateRoutes = !!canGenerate && !isGenerating && (typeof onRegenerateRouteTables === "function" || typeof onGenerate === "function");

  const handleRegenerateRouteTables = () => {
    if (!canRegenerateRoutes) return;
    const fn = typeof onRegenerateRouteTables === "function" ? onRegenerateRouteTables : onGenerate;
    if (typeof fn === "function") fn();
  };

  return (
    <div>
      <style>{`
        @keyframes travelNarrativeSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <button
        onClick={onGenerate}
        disabled={!canGenerate || isGenerating}
        style={{
          padding: "8px 16px",
          backgroundColor: canGenerate && !isGenerating ? "#007BFF" : "#999",
          color: "#fff",
          border: "none",
          borderRadius: "4px",
          cursor: canGenerate && !isGenerating ? "pointer" : "not-allowed",
          marginBottom: "16px",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {isGenerating && (
          <span
            aria-hidden="true"
            style={{
              width: 14,
              height: 14,
              border: "2px solid rgba(255,255,255,0.5)",
              borderTopColor: "#fff",
              borderRadius: "50%",
              display: "inline-block",
              animation: "travelNarrativeSpin 0.8s linear infinite"
            }}
          />
        )}
        {isGenerating
          ? "Generating narrative..."
          : travelNarrativeDraft || routeTablesDraft
          ? "Regenerate narrative"
          : "Generate narrative"}
      </button>

      {isGenerating && (
        <div
          role="status"
          aria-live="polite"
          style={{
            margin: "-6px 0 12px",
            padding: "10px 12px",
            borderRadius: 8,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            fontSize: 13,
            fontWeight: 700
          }}
        >
          Generating and saving travel narrative, route tables, checklist, and prompt CSV...
        </div>
      )}

      {isLoadingSaved && (
        <div style={{ fontSize: 12, color: "#555", margin: "6px 0 10px" }}>
          Loading saved narrative & route tables…
        </div>
      )}

      {travelNarrativeDraft?
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, marginBottom: 10 }}>Travel narrative (editable)</h3>

          <textarea
            ref={narrativeRef}
            value={travelNarrativeDraft}
            onChange={(e) => setTravelNarrativeDraft(e.target.value)}
            style={{
              width: "98%",
              minHeight: "10px",
              maxHeight: "300px",
              overflowY: "auto",
              resize: "none",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #ddd",
              fontFamily: "inherit",
            }}
          />

          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={handleSaveNarrative}
              disabled={isSavingNarrative}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: "none",
                background: "#10B981",
                color: "#fff",
                fontWeight: 600,
                cursor: isSavingNarrative ? "not-allowed" : "pointer",
              }}
            >
              {isSavingNarrative ? "Saving..." : "Save narrative"}
            </button>

            {!!narrativeSaveMsg && <span style={{ color: "#111" }}>{narrativeSaveMsg}</span>}
          </div>

        </div>
        :
        null
            }

      <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: 0, marginBottom: 10 }}>Route tables (saved, editable JSON)</h3>

        {hasRouteTablesDraft ? (
          <textarea
            ref={routeTablesRef}
            value={routeTablesDraft}
            onChange={(e) => setRouteTablesDraft(e.target.value)}
            style={{
              width: "98%",
              minHeight: "10px",
              maxHeight: "220px",
              overflowY: "auto",
              resize: "none",
              padding: 10,
              borderRadius: 8,
              border: "1px solid #ddd",
              fontFamily: "inherit",
            }}
          />
        ) : (
          <div
            style={{
              border: "1px dashed #cbd5f5",
              borderRadius: 8,
              padding: "14px 16px",
              background: "#f8fafc",
              color: "#1f2937",
              fontSize: 14,
            }}
          >
            No route tables have been saved for this location yet. Regenerate them to populate this section.
          </div>
        )}

        <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <button
            onClick={handleSaveRouteTables}
            disabled={isSavingRouteTables || !hasRouteTablesDraft}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: "none",
              background: hasRouteTablesDraft ? "#0b5fff" : "#94a3b8",
              color: "#fff",
              fontWeight: 600,
              cursor: isSavingRouteTables || !hasRouteTablesDraft ? "not-allowed" : "pointer",
            }}
          >
            {isSavingRouteTables ? "Saving..." : "Save route tables"}
          </button>

          <button
            onClick={handleRegenerateRouteTables}
            disabled={!canRegenerateRoutes}
            style={{
              padding: "8px 14px",
              borderRadius: 6,
              border: "none",
              background: canRegenerateRoutes ? "#2563eb" : "#94a3b8",
              color: "#fff",
              fontWeight: 600,
              cursor: canRegenerateRoutes ? "pointer" : "not-allowed",
            }}
          >
            {isGenerating ? "Regenerating..." : "Regenerate route tables"}
          </button>

          {!!routeTablesSaveMsg && <span style={{ color: "#111" }}>{routeTablesSaveMsg}</span>}
        </div>

        {routeTablesPreviewError && (
          <div style={{ marginTop: 10, color: "#b91c1c", fontSize: 13 }}>
            {routeTablesPreviewError}
          </div>
        )}

        {previewRows.length > 0 && !routeTablesPreviewError && (
          <div style={{ marginTop: 18 }}>
            <h4 style={{ margin: "0 0 8px 0" }}>Route tables preview</h4>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px 6px", borderBottom: "1px solid #d1d5db" }}>
                      Route
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 6px", borderBottom: "1px solid #d1d5db" }}>
                      Exit
                    </th>
                    <th style={{ textAlign: "left", padding: "8px 6px", borderBottom: "1px solid #d1d5db" }}>
                      Times
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, idx) => {
                    const path = Array.isArray(row?.path) ? row.path.join(" -> ") : "";
                    const exitName = row?.exitName || row?.exit || row?.exit_name || "";
                    return (
                      <tr key={row?.id ?? idx}>
                        <td style={{ padding: "8px 6px", borderBottom: "1px solid #e5e7eb", whiteSpace: "normal" }}>
                          {path || <span style={{ color: "#6b7280" }}>—</span>}
                        </td>
                        <td style={{ padding: "8px 6px", borderBottom: "1px solid #e5e7eb" }}>
                          {exitName || <span style={{ color: "#6b7280" }}>—</span>}
                        </td>
                        <td style={{ padding: "8px 6px", borderBottom: "1px solid #e5e7eb" }}>
                          <div>Leave (on time): {row?.leaveOnTime || row?.onTime || row?.on_time || "—"}</div>
                          <div style={{ color: "#b45309" }}>10 min late: {row?.leave10MinLate || row?.late10min || row?.late_10_min || "—"}</div>
                          {row?.fft && (
                            <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>Free-flow: {row.fft}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {(travelNarrativeDraft || routeTablesDraft) && autoChecklistItems?.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
          <h3 style={{ margin: 0, marginBottom: 10 }}>Auto checklist items (saved with route tables)</h3>

          <pre style={{ maxHeight: 260, overflow: "auto", background: "#f9f9f9", padding: 8 }}>
            {JSON.stringify(autoChecklistItems, null, 2)}
          </pre>

          <h3 style={{ marginTop: 14, marginBottom: 10 }}>Prompt addition (CSV)</h3>
          <textarea
            value={promptAdditionCsv}
            onChange={(e) => setPromptAdditionCsv(e.target.value)}
            style={{ width: "100%", minHeight: 140, padding: 10, borderRadius: 8, border: "1px solid #ddd" }}
          />
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={handleSavePromptCsvOnly}
              disabled={isSavingPromptCsv}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                border: "none",
                background: "#0b5fff",
                color: "#fff",
                fontWeight: 600,
                cursor: isSavingPromptCsv ? "not-allowed" : "pointer",
              }}
            >
              {isSavingPromptCsv ? "Saving..." : "Save promptCsv only"}
            </button>

            {!!promptCsvSaveMsg && <span style={{ color: "#111" }}>{promptCsvSaveMsg}</span>}
          </div>
        </div>
      )}

      <DownloadablePdfSection
        podcastId={podcastId}
        selectedLanguage={selectedLanguage}
        nodeId={nodeId}
        routeTablesDraft={routeTablesDraft}
      />
    </div>
  );
};

export default TravelNarrativeRouteTablesPanel;
